export interface LearningNode {
  id: string;
  title: string;
  description: string;
  category: LearningCategory;
  prerequisites: string[]; // IDs of nodes that must be completed first
  concepts: ConceptCategory[];
  difficulty: Difficulty;
  estimatedTime: number; // in minutes
  validationCriteria: ValidationCriteria;
  unlockedBy?: string[]; // Alternative unlock conditions
  // Objectif affiché dans le parcours (« X/N exercices réussis »). Valeur
  // unique pour les 3 modes, importée par frontend/src/components/LearningPath.tsx.
  // NB : le seuil réel de complétion d'un nœud reste `getNodeRequirements()` dans
  // backend/src/routes/exercises.ts (voir docs/journal-2026-09/A3-branchement-17-noeuds.md).
  requiredSuccessfulExercises?: number;
}

export interface ValidationCriteria {
  minimumScore: number; // Percentage (0-100)
  requiredAttempts: number; // Minimum number of exercises to complete
  mustDemonstrate: string[]; // Specific skills that must be shown
}

export interface LearningPath {
  id: string;
  name: string;
  description: string;
  targetAudience: string;
  estimatedDuration: string; // "2-3 weeks"
  nodes: LearningNode[];
  milestones: Milestone[];
}

export interface Milestone {
  id: string;
  name: string;
  description: string;
  requiredNodes: string[]; // Node IDs that must be completed
  reward?: string; // Badge or certificate name
  unlocksPath?: string; // Next learning path that becomes available
}

export interface LearningNodeProgress {
  userId: string;
  nodeId: string;
  status: NodeStatus;
  completedAt?: Date;
  attemptsCount: number;
  successfulExercisesCount: number; // number of exercises completed successfully (score >= 70%)
  bestScore: number;
  timeSpent: number; // in minutes
  lastAttemptAt: Date;
}

/**
 * Où en est un étudiant sur UN point de connaissance du parcours : ce que la page d'exercice affiche
 * (repère, compteur, annonce « connaissance acquise ») et ce qui décide du bouton « Connaissance suivante ».
 */
export interface KnowledgePointProgressView {
  point: { id: string; name: string };
  /** L'étape qui porte le point (numéro = position dans le parcours du mode). */
  step: { id: string; title: string; number: number; total: number };
  /** Position du point parmi les points ACTIFS de l'étape, dans l'ordre pédagogique. */
  position: { index: number; total: number };
  /** Exercices distincts tentés sur ce point. */
  attempts: number;
  /** Exercices distincts réussis sur ce point. */
  successes: number;
  /** Réussites exigées pour que le point soit acquis. */
  threshold: number;
  acquired: boolean;
  /** Le point qui suit dans l'étape, `null` après le dernier. */
  nextPoint: { id: string; name: string } | null;
  /** L'étape suivante, renseignée seulement après le dernier point ; `open` = l'étudiant peut y entrer. */
  nextStep: { id: string; title: string; number: number; open: boolean } | null;
}

export enum NodeStatus {
  LOCKED = 'locked',
  AVAILABLE = 'available', 
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  MASTERED = 'mastered' // Completed with excellence
}

export enum LearningCategory {
  // Pure C# Fundamentals
  CSHARP_BASICS = 'csharp_basics',
  CSHARP_CONTROL_FLOW = 'csharp_control_flow',
  CSHARP_DATA_STRUCTURES = 'csharp_data_structures',

  // Unity Editor & Basics
  UNITY_EDITOR = 'unity_editor',
  UNITY_GAMEOBJECTS = 'unity_gameobjects',

  // Unity Scripting API
  UNITY_LIFECYCLE = 'unity_lifecycle',
  UNITY_INPUT = 'unity_input',
  UNITY_PHYSICS = 'unity_physics',
  UNITY_ANIMATION = 'unity_animation',
  UNITY_UI = 'unity_ui',

  // Advanced Concepts
  UNITY_ADVANCED = 'unity_advanced',
  GAME_ARCHITECTURE = 'game_architecture',

  // Python/JS Fundamentals
  PYJS_BASICS = 'pyjs_basics',
  PYJS_CONTROL_FLOW = 'pyjs_control_flow',
  PYJS_DATA_STRUCTURES = 'pyjs_data_structures',

  // Godot GDScript
  GODOT_BASICS = 'godot_basics',
  GODOT_CONTROL_FLOW = 'godot_control_flow',
  GODOT_DATA_STRUCTURES = 'godot_data_structures',
  GODOT_NODES = 'godot_nodes',
  GODOT_SIGNALS_ASYNC = 'godot_signals_async',
  GODOT_ADVANCED = 'godot_advanced',
  GODOT_VECTORS = 'godot_vectors',
  GODOT_INSTANCING = 'godot_instancing',
  GODOT_MOVEMENT = 'godot_movement',
  GODOT_OOP = 'godot_oop'
}

// Import existing types
import { ConceptCategory, Difficulty } from './index';
